/**
 * PropertyHero — Tier 0 slice-local component (SCRUM-167 epic).
 *
 * The radial-wash hero card at the top of `/properties/:id`. Per
 * `~/Desktop/design_handoff_properties/PROPERTY_DETAIL_SPEC.md` Layout
 * + `TOKENS.md` "Hero radial wash" recipe:
 *
 *   - Card background: `--bg-surface` + a radial-gradient wash of
 *     brand-tinted color in the top-right corner
 *   - Top row: type tile (40×40 brand-filled square + icon) + alias
 *     + status badge
 *   - H1: `name` (28/600/-0.4)
 *   - Address line (single line, secondary color)
 *   - Hero action row: Preview / AI assist / Edit status / New unit
 *     buttons (right-aligned)
 *   - Bottom: stat strip (Units / Spaces / Photos / Bookable),
 *     separated by left borders
 *
 * The "Edit status" button is the entry point for `UnitStatusModal`
 * (SCRUM-170). "Preview" + "AI assist" + "New unit" are stubs this
 * ticket — they alert "Coming soon" + cite the right Jira follow-up.
 *
 * The 4-stat strip composes the slice's `<Stat>` primitive. Today
 * `Units` and `Spaces` are shell (BE has no Unit/Space entity); we
 * render them as "1" + "—" with the bundle's understanding that
 * Property IS the unit (per the SCRUM-167 epic matrix). `Photos`
 * + `Bookable` are wired (Photos = `photoCount`; Bookable = derived
 * from `status`).
 */

import * as React from 'react';
import { Button, Icon } from '@sparta/ui-kit';
import { TypeIcon } from './TypeIcon.js';
import { UnitStatus } from './UnitStatus.js';
import { Stat } from './Stat.js';
import { RequirePermission } from '../../../shared/auth/RequirePermission.js';
import type { PropertyView } from '../api/types.js';

export interface PropertyHeroProps {
  readonly property: PropertyView;
  readonly onEditStatus: () => void;
}

export function PropertyHero({
  property,
  onEditStatus,
}: PropertyHeroProps): React.ReactElement {
  const addressLine = [
    property.address.line1,
    [
      property.address.city,
      property.address.stateOrProvince,
      property.address.postalCode,
    ]
      .filter(Boolean)
      .join(', '),
  ]
    .filter(Boolean)
    .join(', ');

  const bookableLabel =
    property.status.op === 'active' && property.status.av === 'bookable'
      ? '1'
      : '0';

  return (
    <section
      aria-labelledby="property-hero-title"
      className="relative rounded-2xl overflow-hidden mb-4"
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-subtle)',
        boxShadow: 'var(--shadow-sm)',
      }}
    >
      {/* Radial wash (decorative). Per TOKENS.md "Hero radial wash". */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 700px 280px at 92% -10%, color-mix(in oklab, var(--brand-500) 14%, transparent), transparent 70%)',
        }}
      />
      <div className="relative px-6 pt-6 pb-0">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <span
              className="inline-flex items-center justify-center rounded-md text-white w-10 h-10"
              style={{ background: 'var(--brand-500)' }}
              aria-hidden
            >
              <TypeIcon type={property.type} size={20} />
            </span>
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className="text-xs font-bold uppercase"
                  style={{
                    color: 'var(--fg-tertiary)',
                    letterSpacing: '0.08em',
                  }}
                >
                  {property.type}
                </span>
                {property.alias !== undefined ? (
                  <span
                    className="text-xs font-mono"
                    style={{ color: 'var(--fg-secondary)' }}
                  >
                    {property.alias}
                  </span>
                ) : null}
                <UnitStatus
                  op={property.status.op}
                  av={property.status.av}
                  size="sm"
                />
              </div>
              <h1
                id="property-hero-title"
                className="m-0 text-2xl font-semibold"
                style={{
                  color: 'var(--fg-primary)',
                  letterSpacing: '-0.025em',
                }}
              >
                {property.name}
              </h1>
              <p
                className="m-0 text-sm"
                style={{ color: 'var(--fg-secondary)' }}
              >
                {addressLine}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                alert(
                  'Preview listing — coming soon (SCRUM-167 follow-up).',
                );
              }}
            >
              <Icon name="eye" size={14} aria-hidden />
              <span className="ml-2">Preview</span>
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                alert(
                  'AI assist — not connected yet (awaiting SCRUM-186 AI endpoint).',
                );
              }}
            >
              <Icon name="sparkles" size={14} aria-hidden />
              <span className="ml-2">AI assist</span>
            </Button>
            {/* Edit status drives PATCH /properties/:id, which the BE
                gates on `property.write`. The detail route is only
                `property.read`-gated, so gate the affordance here so
                read-only users don't see an action the API will 403
                (Simon review #4). */}
            <RequirePermission slug="property.write" fallback={null}>
              <Button variant="secondary" size="sm" onClick={onEditStatus}>
                <Icon name="settings" size={14} aria-hidden />
                <span className="ml-2">Edit status</span>
              </Button>
            </RequirePermission>
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                alert(
                  'New unit — coming soon (awaiting SCRUM-188 Spaces+Units BE entity).',
                );
              }}
            >
              <Icon name="plus" size={14} aria-hidden />
              <span className="ml-2">New unit</span>
            </Button>
          </div>
        </div>
        {/* Stat strip — separated from the title block by a subtle
            divider. Mobile renders as a 2×2 grid (per the bundle's
            IMPLEMENTATION_CHECKLIST mobile contract); sm+ unfolds to
            a 4-up flex row. Photos sits in the bottom-left grid cell
            on mobile (so its left border is suppressed) and the third
            slot of the flex row on sm+ (so the border returns). */}
        <div
          className="mt-4 grid grid-cols-2 sm:flex sm:flex-wrap"
          style={{ borderTop: '1px solid var(--border-subtle)' }}
        >
          <Stat label="Units" value="1" />
          <Stat label="Spaces" value="—" border />
          <Stat
            label="Photos"
            value={String(property.photoCount)}
            border
            className="!border-l-0 sm:!border-l"
          />
          <Stat label="Bookable" value={bookableLabel} border />
        </div>
      </div>
    </section>
  );
}
